import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import CopyToClipboard from 'react-copy-to-clipboard';

import MetricPickerPopup from 'components/Inputs/MetricPicker/MetricPickerPopup';
import { useGetMetricDatasetLabel } from 'components/Inputs/MetricPicker/MetricPickerPopup/hooks/useMetricdatasetLabel';
import MetricEditor from 'components/Inputs/MetricPicker/MetricEditor';
import getTermColor from 'components/MetricsInfoButton/getTermColor';
import Tooltip from 'components/Tooltip';
import usePopup from 'hooks/usePopup';
import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';

import { getMetricLetter } from './expressionAliases';

const List = styled.div`
  border: 1px solid var(--color-border-primary);
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 8px;
`;

const MetricRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background-color: var(--color-bg-primary);

  &:not(:last-child) {
    border-bottom: 1px solid var(--color-border-primary);
  }

  &:hover {
    background-color: var(--color-bg-hover);
  }
`;

const LetterBadge = styled.div<{ color: string }>`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${(props) => props.color};
  color: ${(props) => props.color};
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background-color: var(--color-bg-tag);
  }
`;

const Details = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const IdChip = styled.div`
  display: flex;
  align-items: center;
  max-width: 140px;
  padding: 2px 4px 2px 8px;
  border-radius: 6px;
  background-color: var(--color-bg-tag);
  font-family: monospace;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 20px 16px;
  border: 1px dashed var(--color-border-primary);
  border-radius: 8px;
`;

const describeMetric = ({
  metric,
  datasetLabel,
}: {
  metric: Metrics.NormalMetric;
  datasetLabel: string;
}) => {
  const calculation = `${metric.aggFunc.toLowerCase()}(${metric.field})`;
  return datasetLabel ? `${datasetLabel} · ${calculation}` : calculation;
};

const CopyIdButton = ({ id }: { id: string }) => {
  const [isCopied, setIsCopied] = useState<boolean>(false);

  useEffect(() => {
    if (!isCopied) {
      return;
    }

    const timeout = setTimeout(() => setIsCopied(false), 2000);
    return () => clearTimeout(timeout);
  }, [isCopied]);

  return (
    // Keeps focus (and the caret) in the expression input while copying
    <div onMouseDown={(e) => e.preventDefault()}>
      <Tooltip content={isCopied ? 'Copied' : 'Copy ID'}>
        <CopyToClipboard text={id} onCopy={() => setIsCopied(true)}>
          <Button
            type="Ghost"
            size="X-Small"
            icon={isCopied ? 'checkmark' : 'copy'}
          />
        </CopyToClipboard>
      </Tooltip>
    </div>
  );
};

const InputMetricRow = ({
  metric,
  index,
  datasetLabel,
  onEdit,
  onRemove,
  onInsert,
}: {
  metric: Metrics.NormalMetric;
  index: number;
  datasetLabel: string;
  onEdit: () => void;
  onRemove: () => void;
  onInsert: (letter: string) => void;
}) => {
  const letter = getMetricLetter(index);

  return (
    <MetricRow data-testid={`input-metric-${metric.id}`}>
      {letter && (
        <Tooltip content={`Insert ${letter} into the expression`}>
          <LetterBadge
            color={getTermColor(index)}
            // Keeps focus (and the caret) in the expression input
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onInsert(letter)}
            data-testid={`insert-input-metric-${metric.id}`}
          >
            {letter}
          </LetterBadge>
        </Tooltip>
      )}
      <Details>
        <Typography.Body type="Link" onClick={onEdit} isEllipsis>
          {metric.name}
        </Typography.Body>
        <Typography.Body
          type="Body 12"
          color="var(--color-text-muted)"
          isEllipsis
        >
          {describeMetric({ metric, datasetLabel })}
        </Typography.Body>
      </Details>
      <IdChip>
        <Typography.Body type="Body 12" isEllipsis>
          {metric.id}
        </Typography.Body>
        <CopyIdButton id={metric.id} />
      </IdChip>
      <Tooltip content="Remove">
        <Button
          type="Ghost"
          size="Small"
          icon="cross"
          onClick={onRemove}
          testId={`remove-input-metric-${metric.id}`}
        />
      </Tooltip>
    </MetricRow>
  );
};

const InputMetrics = ({
  selectedMetricIds,
  selectedMetrics,
  onAddMetric,
  onRemoveMetric,
  onInsertMetric,
  onOpened,
}: {
  // Determines each metric's letter in the expression (A = first)
  selectedMetricIds: string[];
  selectedMetrics: Metrics.NormalMetric[];
  onAddMetric: (mId: string) => void;
  onRemoveMetric: (mId: string) => void;
  onInsertMetric: (letter: string) => void;
  onOpened?: () => void;
}) => {
  const { isOpen, open: openPopup, close } = usePopup();
  const getMetricDatasetLabel = useGetMetricDatasetLabel();
  const [selectedMetric, setSelectedMetric] = useState<
    Metrics.NormalMetric | undefined
  >();
  const {
    isOpen: isMetricEditorOpen,
    open: openMetricEditor,
    close: closeMetricEditor,
  } = usePopup();
  const onEditMetricClicked = useCallback(
    (metric: Metrics.NormalMetric) => {
      setSelectedMetric(metric);
      openMetricEditor();
    },
    [openMetricEditor],
  );

  useEffect(() => {
    if (!isMetricEditorOpen) {
      setSelectedMetric(undefined);
    }
  }, [isMetricEditorOpen]);

  const open = useCallback(() => {
    openPopup();
    if (onOpened) {
      onOpened();
    }
  }, [onOpened, openPopup]);

  const AddButton = (
    <Button
      type="Secondary"
      size="Small"
      icon="add"
      onClick={isOpen ? close : open}
      label="Add Metric"
    />
  );

  return (
    <React.Fragment>
      <MetricPickerPopup
        isOpen={isOpen}
        close={close}
        noCompound
        setSelectedMetric={(m) => {
          onAddMetric(m.id);
          close();
        }}
        noSpecial
      />
      <Typography.Body type="Label">
        {selectedMetrics.length > 0
          ? `Input Metrics (${selectedMetrics.length})`
          : 'Input Metrics'}
      </Typography.Body>
      <div style={{ marginBottom: 8 }}>
        <Typography.Body type="Body 12" color="var(--color-text-muted)">
          Click a letter to insert it into the expression.
        </Typography.Body>
      </div>
      {selectedMetrics.length === 0 ? (
        <EmptyState>
          <Typography.Body type="Body 12" color="var(--color-text-muted)">
            No input metrics yet. Add one to start building the expression.
          </Typography.Body>
          {AddButton}
        </EmptyState>
      ) : (
        <>
          <List>
            {selectedMetricIds.map((metricId, index) => {
              const m = selectedMetrics.find((sm) => sm.id === metricId);
              if (!m) {
                return null;
              }

              return (
                <InputMetricRow
                  key={m.id}
                  metric={m}
                  index={index}
                  datasetLabel={getMetricDatasetLabel(m).datasetLabel}
                  onEdit={() => onEditMetricClicked(m)}
                  onRemove={() => onRemoveMetric(m.id)}
                  onInsert={onInsertMetric}
                />
              );
            })}
          </List>
          <div style={{ display: 'inline-block' }}>{AddButton}</div>
        </>
      )}
      <MetricEditor
        isOpen={isMetricEditorOpen}
        close={closeMetricEditor}
        selectedMetric={selectedMetric}
        singleUseEditCancelButtonText="Cancel"
      />
    </React.Fragment>
  );
};

export default InputMetrics;
